import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DestroyService } from '@context/frontend/common';
import { ContentRowComponent } from '@context/frontend/content';
import {
	BreadcrumbsComponent,
	PageTemplateComponent,
} from '@context/frontend/navigation';
import { PaginationPageBase } from '@context/frontend/pagination';
import { TableComponent } from '@context/frontend/table';
import { ExternalViewContextService } from '@context/frontend/user';
import { Content } from '@context/shared/types/common';
import { ContentFolder } from '@context/shared/types/folder';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiButton, TuiIcon } from '@taiga-ui/core';
import { DocumentReference, where } from 'firebase/firestore';
import { takeUntil } from 'rxjs';
import { ExternalContentService } from '../../../services/external-content.service';
import { ExternalViewerNavService } from '../../../services/external-viewer-nav.service';
import { ExternalContentResolved } from '../external-content.resolver';

/**
 * Read-only folder browser for the external viewer. Lists the folder's immediate
 * children (folders + files) through {@link ExternalContentService} using the same
 * pagination/table plumbing as the portal, and exposes breadcrumbs/back navigation
 * that are bounded to the share root.
 */
@Component({
	imports: [
		RouterLink,
		TranslocoModule,
		ContentRowComponent,
		BreadcrumbsComponent,
		TableComponent,
		TuiButton,
		TuiIcon,
		PageTemplateComponent,
	],
	selector: 'ctx-external-folder-view',
	templateUrl: 'external-folder-view.component.html',
	providers: [DestroyService],
	host: {
		class: 'flex flex-col flex-1',
	},
})
export class ExternalFolderViewComponent extends PaginationPageBase<Content> {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly context = inject(ExternalViewContextService);
	protected readonly nav = inject(ExternalViewerNavService);

	readonly folder = signal<ContentFolder | null>(null);
	readonly folderRef = signal<DocumentReference<Content> | null>(null);

	readonly breadcrumbs = signal<ContentFolder[]>([]);

	readonly backFolderId = signal<string | null>(null);

	constructor() {
		super(
			{ key: 'name', enabled: true },
			{
				icon: '@tui.folder-open',
				header: 'external.viewer.folder.empty-header',
				content: 'external.viewer.folder.empty-content',
			},
			inject(ExternalContentService),
		);
	}

	override ngOnInit() {
		this.resetSort();

		// Drive every load from the resolved route data so navigating between
		// folders (paramsChange reuses this component) refetches the children.
		this.route.data
			.pipe(takeUntil(this.destroy$))
			.subscribe((data) => this.onRouteData(data['content']));
	}

	private onRouteData(resolved: ExternalContentResolved | null) {
		if (!resolved) return;

		// A file id reached the folder route - send the viewer to the file view.
		if (resolved.data.type !== 'folder') {
			this.router.navigate(this.nav.contentLink(resolved.data), {
				replaceUrl: true,
			});
			return;
		}

		const folder = resolved.data as ContentFolder;
		this.folder.set(folder);
		this.folderRef.set(resolved.ref);
		this.backFolderId.set(this.nav.parentWithinBounds(folder));

		void this.loadBreadcrumbs(folder);

		this.determineConstraints(resolved.ref);
		this.onLoadItems({}, true);
	}

	/** Routes each breadcrumb segment within the current share. */
	protected readonly breadcrumbLink = (segment: Content): unknown[] =>
		this.nav.folderLink(segment.id);

	private async loadBreadcrumbs(folder: ContentFolder) {
		try {
			// boundedBreadcrumbs returns the trail down to (and including) the
			// current folder. The current folder is shown as the heading title, so
			// the breadcrumb trail is just its bounded ancestors.
			const trail = await this.nav.boundedBreadcrumbs(folder);
			this.breadcrumbs.set(trail.slice(0, -1));
		} catch (error) {
			console.error('Failed to build breadcrumbs', error);
			this.breadcrumbs.set([]);
		}
	}

	/**
	 * Scopes the paginated fetch to the current folder's immediate, non-archived
	 * children. The external app never sets the organization context, so the org id
	 * (resolved from the share link) is passed through for the collection lookup.
	 */
	private determineConstraints(folderRef: DocumentReference<Content>) {
		this.setOptions({
			organizationId: this.context.data()?.organizationId ?? null,
			constraints: [
				where('parent', '==', folderRef),
				where('archivedAt', '==', null),
			],
		});
	}
}
